import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useAuth } from '../hooks/useAuth'; // Assuming you have a useAuth hook
import { getFormattedISODateTime } from '../assets/Utils';
import { useNavigate } from 'react-router-dom';
import AdvancedArrayInput from './AdvancedArrayInput';
import SubForm from './widgets/SubForm';
import CodeInput from './widgets/CodeInput';
import SignatureCapture from './widgets/SignatureCapture';
import AiAssistPanel from './AiAssistPanel';
import '../styles/FormModal.css';

const FormModal = ({ initialData, onClose, apiConfig }) => {
  const { user, token, hasPermission } = useAuth();
  const userRole = user?.role;
  const navigate = useNavigate();

  const isFieldVisible = (field, data) => {
    if (field.visible === false) return false;
    if (!field.visibleWhen) return true;

    const { field: dependentField, equals, includes } = field.visibleWhen;
    const dependentValue = data[dependentField];

    if (equals !== undefined) {
      return dependentValue === equals;
    }

    if (includes !== undefined) {
      return Array.isArray(dependentValue) && dependentValue.includes(includes);
    }

    return true;
  };

  const isFieldRequired = (field, data) => {
    if (typeof field.required === 'function') {
      return field.required(data);
    }
    if (field.requiredWhen) {
      const { field: dependentField, equals } = field.requiredWhen;
      return data?.[dependentField] === equals;
    }
    return !!field.required;
  };

  const formatDateForInput = (date) => {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate)) {
      return parsedDate.toISOString().split('T')[0]; // Convert to YYYY-MM-DD format
    }
    return date;
  };

  const initializeFormData = () => {
    const data = initialData || {};

    //console.log("Data before initialization: ",data);
    apiConfig.fields.forEach((field) => {
      let value = data[field.name];

      if ((value === undefined || value === null) && field.defaultValue !== undefined) {
        value = field.defaultValue;
        // console.log("Just set a default value for ",field.name," with the value: ",field.defaultValue);
      }

      // Format date fields for input if they already have a value
      if (field.type === 'date' && value) {
        value = formatDateForInput(value);
      }

      // Parse JSON fields if they are stored as strings
      if (field.type === 'advanced-array' || field.type === 'subform' || field.type === 'checkbox-group') {
        if (typeof value === 'string') {
          try {
            // First parse to remove the extra layer of quotes if necessary
            const parsedValue = JSON.parse(value);
            console.log("Parsed Value:", parsedValue, " and type:", typeof parsedValue);
            // Check if parsedValue is still a string and needs another parse
            if (typeof parsedValue === 'string') {
              value = JSON.parse(parsedValue);
            } else {
              value = parsedValue;
            }
          } catch (e) {
            console.warn(`Failed to parse field ${field.name}`, e);
          }
        }

        // Ensure the value is an array for array-backed fields
        if (!Array.isArray(value)) {
          value = [];
        }
      }

      if (typeof data[field.name] === 'object' && field.type !== 'advanced-array' && field.type !== 'subform' && field.type !== 'checkbox-group') {
        // console.log('Found an object: ',field.name," with the value: ",value);
        value = JSON.stringify(value);
      }

      if (field.onInitialize) {
        value = field.onInitialize(value, data);
      }

      if (value === "null") value = null;

      data[field.name] = value;
    });
    console.log('Initial Data:', data);
    return data;
  };

  const isNewRecord = !initialData || !apiConfig.fields.find((f) => f.identifier && initialData[f.name] != null);
  const [formData, setFormData] = useState(initializeFormData());
  const [lastSavedData, setLastSavedData] = useState(isNewRecord ? null : initialData);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState({});

  // AI Assist state
  const [aiPanelVisible, setAiPanelVisible] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiCached, setAiCached] = useState(false);
  const [activeAiAction, setActiveAiAction] = useState(null);

  // Determine the initial active tab
  const accessibleTabs = (apiConfig.tabs || []).filter((tabConfig) => {
    // Priority 1: Check for backwards compatibility with roles
    if (tabConfig.roles) {
      if (tabConfig.roles === 'all') {
        return true; // Bypass security check for "all"
      }
      const allowedRoles = tabConfig.roles.split(',').map((role) => role.trim());
      return allowedRoles.includes(userRole);
    }
    
    // Priority 2: Check securityPath if no roles specified
    if (tabConfig.securityPath) {
      return hasPermission(tabConfig.securityPath, tabConfig.permission || 'View');
    }
    
    // Default: Allow access if neither roles nor securityPath specified
    return true;
  });

  const defaultTab =
    accessibleTabs.find((tabConfig) => tabConfig.default) || accessibleTabs[0] || { name: 'Main' };

  const getTabConfig = (tabName) =>
    (apiConfig.tabs || []).find((tabConfig) => tabConfig.name === tabName);

  const isFormReadOnly = () =>
    typeof apiConfig.isReadOnly === 'function' && apiConfig.isReadOnly(formData);

  const isFieldDisabled = (field) => {
    if (isFormReadOnly()) return true;
    if (field.disabled) return true;

    const tabConfig = getTabConfig(field.tab || 'Main');
    if (tabConfig?.securityPath) {
      const editAction = tabConfig.editPermission || 'Edit';
      return !hasPermission(tabConfig.securityPath, editAction);
    }

    return false;
  };

  const [activeTab, setActiveTab] = useState(defaultTab.name);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find AI action for the current active tab
  const currentAiAction = (apiConfig.aiActions || []).find(a => a.tab === activeTab);

  // AI Assist handler
  const handleAiAssist = async (aiAction) => {
    setActiveAiAction(aiAction);
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions(null);
    setAiCached(false);
    setAiPanelVisible(true);

    try {
      const payload = aiAction.payloadBuilder(formData);
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}${aiAction.endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAiSuggestions(response.data.data);
        setAiCached(response.data.cached || false);
      } else {
        setAiError(response.data.error || 'Unexpected error');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to get AI suggestions';
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiApply = (fieldUpdates) => {
    setFormData(prev => ({ ...prev, ...fieldUpdates }));
    setStatusMessage({ type: 'ai-info', text: '✨ AI suggestion applied. Review and save when ready.' });
  };

  const handleAiApplyAll = (fieldUpdates) => {
    setFormData(prev => ({ ...prev, ...fieldUpdates }));
    setAiPanelVisible(false);
    setStatusMessage({ type: 'ai-info', text: '✨ All AI suggestions applied. Review and save when ready.' });
  };

  useEffect(() => {
    console.log("Form Data:", initialData);
    if (apiConfig.dropdownFields || apiConfig.staticDropdownFields) {
      fetchDropdownOptions();
    } else {
      setLoadingOptions(false);
    }
  }, [apiConfig.dropdownFields?.length, apiConfig.staticDropdownFields?.length]);

  useEffect(() => {
    setFormData(initializeFormData());
  }, [initialData]);

  const fetchDropdownOptions = async () => {
    setLoadingOptions(true);
    const options = {};

    if (apiConfig.staticDropdownFields) {
      for (const field of apiConfig.staticDropdownFields) {
        options[field.name] = field.options.map((item) => ({
          value: item.id,
          label: item.name,
        }));
      }
    }

    if (apiConfig.dropdownFields) {
      for (const field of apiConfig.dropdownFields) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}${field.endpoint}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          // Use transformResponse if provided, otherwise use default mapping
          if (field.transformResponse) {
            options[field.name] = field.transformResponse(response.data);
          } else {
            options[field.name] = response.data.map((item) => ({
              value: item[field.valueField],
              label: item[field.labelField],
            }));
          }
        } catch (error) {
          console.error(`Error fetching options for ${field.name}:`, error);
        }
      }
    }

    setDropdownOptions(options);
    setLoadingOptions(false);
  };

  const getIdentifierValue = () => {
    const identifierField = apiConfig.fields.find((field) => field.identifier);
    return identifierField ? formData[identifierField.name] : null;
  };

  const handleCheckboxGroupChange = (field, optionId, checked) => {
    const currentValues = Array.isArray(formData[field.name]) ? formData[field.name] : [];
    const newValues = checked
      ? [...currentValues, optionId]
      : currentValues.filter((value) => value !== optionId);

    const updatedFormData = { ...formData, [field.name]: newValues };
    setFormData(updatedFormData);

    if (field.onChange) {
      field.onChange(newValues, updatedFormData, setFormData, field.name);
    }
  };

  const handleInputChange = (field, e) => {
    const { name, value, type, checked, files } = e.target;
    let fieldValue;

    if (type === 'checkbox') {
      fieldValue = checked;
    } else if (type === 'file') {
      fieldValue = files[0] || null; // Handle file input (single file for now)
    } else {
      fieldValue = value ?? '';
    }

    const updatedFormData = { ...formData, [name]: fieldValue };

    setFormData(updatedFormData);

    if (field.onChange) {
      field.onChange(fieldValue, updatedFormData, setFormData, name);
    }
  };

  const handleAdvancedArrayInputChange = (name, value) => {
    console.log("Advanced Array Input Change:", name, value);
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    const field = apiConfig.fields.find(field => field.name === name);
    if (field?.onChange) {
      field.onChange(value, updatedFormData, setFormData, name);
    }
  };

  const handleSubFormChange = (name, value) => {
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    
    const field = apiConfig.fields.find(field => field.name === name);
    if (field?.onChange) {
      field.onChange(value, updatedFormData, setFormData, name);
    }
  };

  const handleCodeChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSignatureChange = (name, value) => {
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    
    const field = apiConfig.fields.find(field => field.name === name);
    if (field?.onChange) {
      field.onChange(value, updatedFormData, setFormData, name);
    }
  };

  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;
    const recordId = getIdentifierValue();
    const bucketIdValue = formData[apiConfig.gcBucketIdentifier];
    const fileMainId = bucketIdValue ? bucketIdValue : recordId;
    if (fileMainId != null) {
      const previousPath = formData[fieldName];
      const filePath = bucketIdValue
        ? `${apiConfig.gcBucketName}/${fileMainId}/${fieldName}/${recordId}`
        : `${apiConfig.gcBucketName}/${fileMainId}/${fieldName}`;
      const payloadData = new FormData();
      payloadData.append('file', file);
      payloadData.append('filePath', filePath);
      payloadData.append('id', recordId);
      payloadData.append('columnName', fieldName);

      setFormData((prev) => ({ ...prev, [fieldName]: null }));

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/documents/lab-results/upload-pdf`,
          payloadData,
          {
            onUploadProgress: (progressEvent) => {
              const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentage);
            },
          }
        );
        setFormData((prev) => ({ ...prev, [fieldName]: response.data.path }));
      } catch (error) {
        console.error('Error uploading file:', error);
        setFormData((prev) => ({ ...prev, [fieldName]: previousPath || null }));
        alert(error.response?.data?.error || 'Failed to upload file.');
      } finally {
        setUploadProgress(0);
      }
    } else {
      setFormData((prev) => ({ ...prev, [fieldName]: null }));
      alert('Record must be saved before uploading any files!');
    }
  };

  const handleDropdownChange = (selectedOption, action) => {
    
    const updatedFormData = { ...formData, [action.name]: selectedOption?.value || '' };
    setFormData(updatedFormData);

    const field = apiConfig.fields.find((field) => field.name === action.name);
    if (field.onChange) {
      field.onChange(selectedOption?.value, updatedFormData, setFormData, name);
    }
  };

  const isFormValid = () => {
    return apiConfig.fields
      .filter((field) => isFieldRequired(field, formData) && isFieldVisible(field, formData))
      .every((field) => {
        if (field.type === 'checkbox-group') {
          return Array.isArray(formData[field.name]) && formData[field.name].length > 0;
        }
        return !!formData[field.name];
      });
  };

  const simulateDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const parseFormDataForSubmission = (data) => {
    const parsedData = { ...data };
    apiConfig.fields.forEach((field) => {
      if (field.type === 'file') {
        return; // Skip file fields
      }
      try {
        // Parse back JSON strings if applicable
        if (typeof parsedData[field.name] === 'string' && field.allowJson) {
          parsedData[field.name] = JSON.parse(parsedData[field.name]);
        }
      } catch (e) {
        console.warn(`Failed to parse field ${field.name}`, e);
      }
    });
    return parsedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormReadOnly()) return;
    setIsSubmitting(true);
    const identifierValue = getIdentifierValue();

    if (apiConfig.additionalValidation) {
      const validationResult = await apiConfig.additionalValidation(formData);
      if (!validationResult.isValid) {
        setStatusMessage({ type: 'error', text: validationResult.message });
        setIsSubmitting(false);
        return;
      }
    }

    // Get the fields that have changed
    const payload = omitHiddenTabFields(getChangedFields(formData, lastSavedData));
    const finalPayload = includeUpdatedBy(payload, user.portal_id);

    try {
      if (identifierValue) {
        // PUT for updates
        await axios.put(
          `${process.env.REACT_APP_SERVER_BASE_URL}${apiConfig.updateEndpoint}/${identifierValue}`,
          finalPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setLastSavedData(formData);
        setStatusMessage({ type: 'success', text: 'Form submitted successfully!' });
        if (apiConfig.onUpdateSuccess) {
          apiConfig.onUpdateSuccess(formData);
        }
      } else {
        // POST for new records
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}${apiConfig.createEndpoint}`,
          finalPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const identifierField = apiConfig.fields.find((fld) => fld.identifier);
        console.log("Identifier name:", identifierField);
        console.log("return ID: ", response.data[identifierField.name]);
        const updatedFormData = {
          ...formData,
          [identifierField.name]: response.data[identifierField.name],
        };
        setFormData(updatedFormData);

        setLastSavedData(updatedFormData);
        setStatusMessage({ type: 'success', text: 'Form submitted successfully!' });
        if (apiConfig.onCreateSuccess) {
          apiConfig.onCreateSuccess(updatedFormData);
        }
      }
    } catch (error) {
      console.error('Error saving record:', error.response.data.error);
      // alert('Failed to save record.');
      setStatusMessage({
        type: 'error',
        text: 'Failed to save data. REASON: ' + error.response.data.error,
      });
    } finally {
      setIsSubmitting(false); // Re-enable the button
    }
  };
  const getChangedFields = (formData, initialData) => {
    const changes = {};
    Object.keys(formData).forEach((key) => {
      const formValue = formData[key];
      const initialValue = initialData?.[key];

      // Check for changes: compare values, handling JSON for nested objects
      if (JSON.stringify(formValue) !== JSON.stringify(initialValue)) {
        changes[key] = formValue;
      }
    });
    // console.log("Changes: ",changes);
    return changes;
  };

  const hasChanges = () => Object.keys(getChangedFields(formData, lastSavedData)).length > 0;

  const handleNavigation = (event, path, linkIdField) => {
    if (hasChanges()) {
      event.preventDefault(); // Prevent the default navigation behavior
      const confirmLeave = window.confirm(
        "You have unsaved changes. Do you really want to leave?"
      );
      if (!confirmLeave) return;
    }

    navigate(path, { state: { id: formData[linkIdField] } });
  };

  const handleRemoveFile = async (fieldName) => {
    const filePath = formData[fieldName];
    if (!filePath) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/documents/lab-results/delete-pdf`,
        { gcPath: filePath, columnName: fieldName, id: getIdentifierValue() }
      );
      setStatusMessage({ type: 'success', text: 'File removed successfully!' });
      setFormData((prev) => ({ ...prev, [fieldName]: null }));
    } catch (error) {
      console.error('Error removing file:', error);
      setStatusMessage({ type: 'error', text: 'Failed to remove file.' });
    }
  };

  const renderFileInput = (field) => {
    const storedPath = typeof formData[field.name] === 'string' ? formData[field.name].trim() : '';
    const fileName = storedPath ? storedPath.split('/').pop() : '';

    return (
      <div key={field.name} className="form-group-horizontal">
        {storedPath ? (
          <div className="file-preview">
            <button type="button" onClick={() => handleDownload(storedPath)}>
              {fileName.length > 30 ? `${fileName.slice(0, 30)}...` : fileName}
            </button>
            <button
              type="button"
              onClick={() => handleRemoveFile(field.name)}
              className="remove-file-button"
            >
              Remove File
            </button>
          </div>
        ) : (
          <input
            type="file"
            name={field.name}
            accept={field.accept || '*'}
            onChange={(e) => handleFileUpload(e.target.files[0], field.name)}
            disabled={!getIdentifierValue()}
          />
        )}
      </div>
    );
  };
  const customStyles = (width) => ({
    control: (provided, state) => ({
      ...provided,
      height: '40px',
      minHeight: '40px',
      backgroundColor: state.isDisabled ? '#e9ecef' : provided.backgroundColor,
      padding: '0px',
      marginBottom: '0px',
      marginRight: '0px',
      width: width || provided.width,
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '40px',
      padding: '0 8px',
    }),
    container: (provided) => ({
      ...provided,
      padding: '0px 0px',
      margin: '0px 0px',
      width: width || provided.width,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1050,
    }),
  });

  const handleDownload = async (filePath) => {
    setUploadProgress(0);
    try {
      // Make a GET request to the download endpoint with Axios
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/documents/download`,
        {
          params: { filePath },
          responseType: 'blob', // ensures the response is treated as a file (binary data)
          onDownloadProgress: (progressEvent) => {
            let percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Download Progress: ${percentage}%`);
            setUploadProgress(percentage); // Update progress state
          },
        }
      );

      // Extract the file name from the filePath or Content-Disposition header
      const fileName = response.headers['content-disposition']
        ? response.headers['content-disposition']
            .split('filename=')[1]
            ?.replace(/['"]/g, '') // Clean up quotes if present
        : filePath.split('/').pop();

      // Create a Blob URL for the file
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const blobUrl = window.URL.createObjectURL(blob);

      // Create an anchor element to trigger the download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'downloaded-file'; // Fallback if fileName is undefined
      document.body.appendChild(link); // Append link to the DOM
      link.click(); // Programmatically trigger the click event
      document.body.removeChild(link); // Clean up the DOM

      // Revoke the Blob URL after the download starts
      window.URL.revokeObjectURL(blobUrl);

      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(0);
      }, 500);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
      setUploadProgress(0); // Reset progress in case of error
    }
  };

  const ensureOptionIncluded = (field, value) => {
    const options = dropdownOptions[field.name] || [];
    const exists = options.some((option) => option.value === value);
    if (!exists && value) {
      const newOption = { value, label: value };
      options.push(newOption);
      setDropdownOptions({ ...dropdownOptions, [field.name]: options });
    }
  };

  const groupedFields = apiConfig.fields.reduce((groups, field) => {
    const tab = field.tab || 'Main';
    if (!groups[tab]) groups[tab] = [];
    groups[tab].push(field);
    return groups;
  }, {});

  const omitHiddenTabFields = (data) => {
    const next = { ...data };
    const keepHiddenOnCreate = isNewRecord && Array.isArray(next.__keepHiddenOnCreate)
      ? new Set(next.__keepHiddenOnCreate)
      : new Set();
    delete next.__keepHiddenOnCreate;

    if (!apiConfig.tabs?.length) return next;
    const allowedTabs = new Set(accessibleTabs.map((tabConfig) => tabConfig.name));
    const tabsByName = new Map();
    apiConfig.fields.forEach((field) => {
      const tab = field.tab || 'Main';
      if (!tabsByName.has(field.name)) tabsByName.set(field.name, []);
      tabsByName.get(field.name).push(tab);
    });
    tabsByName.forEach((tabs, name) => {
      const keptOnAccessibleTab = tabs.some((tab) => allowedTabs.has(tab));
      if (keptOnAccessibleTab) return;
      const value = next[name];
      const keepOnCreate = keepHiddenOnCreate.has(name)
        && value != null
        && String(value).trim() !== '';
      if (!keepOnCreate) delete next[name];
    });
    return next;
  };

  const includeUpdatedBy = (data, userId) => ({
    ...data,
    updatedBy: userId,
  });

  const handleClose = (e) => {
    if (hasChanges()) {
      e.preventDefault();
      const confirmLeave = window.confirm(
        "You have unsaved changes. Do you really want to leave?"
      );
      if (!confirmLeave) return;
    }
    onClose();
  };

  const getFormTitle = () => {
    if (getIdentifierValue()) {
      //this is an edit operation
      if (apiConfig.formTitle != '') {
        //custom title
        return `Editing ${apiConfig.formTitle}`;
      } else {
        return `Editing Record ID:  ${getIdentifierValue()}`;
      }
    } else {
      return `Add ${apiConfig.title}`;
    }
  };
  return (
    <>
    <div className="modal form-modal">
      <div className="modal-content">
        <h3>{getFormTitle()}</h3>

        <div className="form-modal-tabs">
          <div className="form-modal-tabs-list" role="tablist">
            {accessibleTabs.map((tabConfig) => (
              <button
                key={tabConfig.name}
                type="button"
                role="tab"
                aria-selected={activeTab === tabConfig.name}
                className={`tab-button ${activeTab === tabConfig.name ? 'active' : ''}`}
                onClick={() => setActiveTab(tabConfig.name)}
              >
                {tabConfig.name}
              </button>
            ))}
          </div>
          {currentAiAction && !isFormReadOnly() && hasPermission(currentAiAction.permissionKey, currentAiAction.permissionAction) && (
            <div className="form-modal-tabs-actions">
              <button
                type="button"
                className={`ai-assist-trigger${currentAiAction.buttonIcon ? ' ai-assist-trigger--icon-only' : ''}`}
                onClick={() => handleAiAssist(currentAiAction)}
                disabled={aiLoading || !formData[apiConfig.fields.find(f => f.identifier)?.name]}
                title={!formData[apiConfig.fields.find(f => f.identifier)?.name] ? 'Save the record first' : (currentAiAction.buttonLabel || 'Get AI suggestions')}
              >
                <span className="ai-sparkle">{currentAiAction.buttonIcon || '✨'}</span>
                {!currentAiAction.buttonIcon && (currentAiAction.buttonLabel || 'Refine Notes')}
              </button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="form-modal-form">
          <div className="form-modal-body">
          {(apiConfig.tabAlerts || [])
            .filter((alert) => alert.tab === '*' || alert.tab === activeTab)
            .map((alert, index) => {
              const message = alert.getMessage?.(formData);
              if (!message) return null;
              return (
                <div
                  key={`${alert.tab}-${index}`}
                  className={`form-tab-alert ${alert.className || 'form-tab-alert--abnormal-vitals'}`}
                  role="status"
                >
                  {message}
                </div>
              );
            })}
          {groupedFields[activeTab]?.map((field) => {
            if (!isFieldVisible(field, formData)) {
              return null;
            }

            return (
            <div className={`form-group-horizontal${field.className ? ` ${field.className}` : ''}`} key={field.name}>
              <label style={{ color: isFieldRequired(field, formData) ? 'red' : 'inherit' }}>
                {field.label}
                {isFieldRequired(field, formData) && '*'}
              </label>
              {field.type === 'code' ? (
                <CodeInput
                  length={field.length || 6}
                  value={formData[field.name] || ''}
                  onChange={(value) => handleCodeChange(field.name, value)}
                  autoFocus={field.autoFocus}
                />
              ) : field.type === 'dropdown' ? (
                loadingOptions ? (
                  <p>Loading options...</p>
                ) : (
                  (() => {
                    // Get the options from your source.
                    let options = dropdownOptions[field.name] || [];
                    // If a defaultValue is specified, prepend it as an option.
                    console.log("Default Value Check:", field.defaultValue);
                    if (field.defaultValue) {
                      const alreadyPresent = options.some(
                        (option) => String(option.value) === String(field.defaultValue)
                      );
                      if (!alreadyPresent) {
                        options = [
                          { value: field.defaultValue, label: String(field.defaultValue) },
                          ...options,
                        ];
                      }
                    }
                    const currentValue = formData[field.name] ?? field.defaultValue;
                    
                    // Determine if field should be creatable based on permission
                    const isCreatable = field.isCreatable && 
                      (!field.creatablePermission || hasPermission(field.creatablePermission, 'Add'));
                    
                    const SelectComponent = isCreatable ? CreatableSelect : Select;
                    
                    return (
                      <SelectComponent
                        name={field.name}
                        options={options}
                        value={
                          options.find((option) => String(option.value) === String(currentValue)) ||
                          (currentValue ? { value: currentValue, label: currentValue } : null)
                        }
                        onChange={handleDropdownChange}
                        isClearable
                        placeholder={`Select ${field.label}`}
                        styles={customStyles(field.width)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        isDisabled={isFieldDisabled(field)}
                        hidden={field.visible === false}
                      />
                    );
                  })()
                )
              ) : field.type === 'checkbox-group' ? (
                <div className="checkbox-group" role="group" aria-label={field.label}>
                  {(field.options || []).map((option) => (
                    <label key={option.id} className="checkbox-group-option">
                      <input
                        type="checkbox"
                        checked={Array.isArray(formData[field.name]) && formData[field.name].includes(option.id)}
                        onChange={(e) => handleCheckboxGroupChange(field, option.id, e.target.checked)}
                        disabled={isFieldDisabled(field)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              ) : field.type === 'checkbox' ? (
                <input
                  type="checkbox"
                  name={field.name}
                  checked={!!formData[field.name]}
                  onChange={(e) => handleInputChange(field, e)}
                  disabled={isFieldDisabled(field)}
                  hidden={field.visible === false}
                />
              ) : field.type === 'file' ? (
                renderFileInput(field)
              ) : field.type === 'textarea' ? (
                <textarea
                  name={field.name}
                  value={
                    formData[field.name] === null || formData[field.name] === undefined
                      ? '' // Set empty string for null or undefined
                      : formData[field.name]
                  }
                  onChange={(e) => handleInputChange(field, e)}
                  disabled={isFieldDisabled(field)}
                  rows={field.maxRows || 3}
                  hidden={field.visible === false}
                  placeholder={field.placeholder || ''}
                />
              ) : field.type === 'datetime-local' ? (
                <input
                  type={field.type}
                  name={field.name}
                  value={getFormattedISODateTime(formData[field.name] ?? '')}
                  onChange={(e) => handleInputChange(field, e)}
                  disabled={isFieldDisabled(field)}
                  hidden={field.visible === false}
                />
              ) : field.type === 'link' ? (
                <span
                  className="custom-link"
                  onClick={(event) =>
                    handleNavigation(event, field.path, field.linkIdField)
                  }
                >
                  {field.label}
                </span>
              ) : field.type === 'advanced-array' ? (
                <AdvancedArrayInput
                  key={field.name}
                  endpoint={field.endpoint}
                  label={field.label}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleAdvancedArrayInputChange}
                  showItemNotFoundWarning={field.showItemNotFoundWarning}
                  additionalFields={field.additionalFields || []}
                  disabled={isFieldDisabled(field)}
                />
              ) : field.type === 'subform' ? (
                <SubForm
                  name={field.name}
                  value={formData[field.name] || []}
                  onChange={handleSubFormChange}
                  fields={field.subFields || []}
                  fixedRows={field.fixedRows || false}
                  disabled={isFieldDisabled(field)}
                />
              ) : field.type === 'signature' ? (
                <SignatureCapture
                  name={field.name}
                  label={field.label}
                  value={formData[field.name] || ''}
                  onChange={handleSignatureChange}
                  disabled={isFieldDisabled(field)}
                  required={isFieldRequired(field, formData)}
                  width={field.width || 400}
                  height={field.height || 150}
                />
              ) : (
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name] ?? ''}
                  onChange={(e) => handleInputChange(field, e)}
                  disabled={isFieldDisabled(field)}
                  hidden={field.visible === false}
                  autoComplete="off"
                  placeholder={field.placeholder || ''}
                />
              )}
            </div>
            );
          })}
          </div>
          <div className="form-actions">
            <button
              type="submit"
              disabled={!isFormValid() || loadingOptions || isSubmitting || isFormReadOnly()}
            >
              Save
            </button>
            <button type="button" onClick={(e) => handleClose(e)}>
              {hasChanges() ? 'Cancel' : 'Close'}
            </button>
          </div>
          {uploadProgress > 0 && (
            <div className="progress-bar">
              <div className="progress" style={{ width: `${uploadProgress}%` }}>
                Processing ...
              </div>
            </div>
          )}
          {statusMessage && statusMessage.text && (
            <div
              className={`status-message ${
                statusMessage.type === 'error' ? 'error' : statusMessage.type === 'ai-info' ? 'ai-info' : 'success'
              }`}
            >
              {statusMessage.text}
            </div>
          )}
        </form>
      </div>
    </div>
    {createPortal(
      <AiAssistPanel
        visible={aiPanelVisible}
        onClose={() => setAiPanelVisible(false)}
        onApply={handleAiApply}
        onApplyAll={handleAiApplyAll}
        suggestions={aiSuggestions}
        loading={aiLoading}
        error={aiError}
        cached={aiCached}
        fieldMapping={activeAiAction?.fieldMapping}
        onRetry={() => activeAiAction && handleAiAssist(activeAiAction)}
        mode={activeAiAction?.mode || 'suggestions'}
        sections={activeAiAction?.sections}
        panelTitle={activeAiAction?.panelTitle}
        reasoningKey={activeAiAction?.reasoningKey}
        confidenceKey={activeAiAction?.confidenceKey}
      />,
      document.body
    )}
    </>
  );
};

export default FormModal;
